import { z } from 'zod'
import {
  MAX_ROOMS_PER_BLOCK,
  MAX_TEMPLATE_EXTRAS,
  MAX_TEMPLATE_GALLERY_PHOTOS,
  TEMPLATE_IDS
} from '../config'
import { isGoogleMapsEmbedUrl } from '../../google-location/utils/embedUrl'
import {
  MAP_HEIGHTS,
  assetRefSchema,
  idSchema,
  openingHoursEntrySchema,
  roomPlanSchema
} from './elements'
import { localizedTextSchema } from './localized'

/**
 * The sections a template page is made of, in the words every template uses.
 *
 * A template decides the order, which variants it offers and how each section
 * is dressed; these are the owner's choices about them, kept apart from the
 * template so that switching templates keeps every choice that still applies.
 */
export const SECTION_KEYS = [
  'hero',
  'menu',
  'about',
  'visit',
  'gallery',
  'reservations',
  'map',
  'review'
] as const

export type SectionKey = (typeof SECTION_KEYS)[number]

export const HERO_VARIANTS = ['photoBackground', 'split', 'banner'] as const
export const ABOUT_VARIANTS = ['photoLeft', 'photoRight', 'photoAbove'] as const
export const VISIT_VARIANTS = ['sideBySide', 'stacked'] as const
export const GALLERY_VARIANTS = ['grid', 'strip'] as const
export const MENU_VARIANTS = ['list', 'grid'] as const

export type HeroVariant = (typeof HERO_VARIANTS)[number]
export type AboutVariant = (typeof ABOUT_VARIANTS)[number]
export type VisitVariant = (typeof VISIT_VARIANTS)[number]
export type GalleryVariant = (typeof GALLERY_VARIANTS)[number]
export type MenuVariant = (typeof MENU_VARIANTS)[number]

const toggled = z.object({ enabled: z.boolean().default(true) })

/**
 * One of the owner's own blocks: a text, or a photo with a text, after one of
 * the template's sections. The one thing on the page that is theirs to place,
 * and it is placed by naming a section rather than by dragging, so it follows
 * that section wherever the next template puts it.
 */
export const extraBlockSchema = z.object({
  id: idSchema,
  after: z.enum(SECTION_KEYS),
  heading: localizedTextSchema.optional(),
  /** Plain text; a line break is a paragraph, as in the description. */
  text: localizedTextSchema,
  photo: assetRefSchema.optional(),
  photoSide: z.enum(['left', 'right']).default('left')
})

export type ExtraBlock = z.infer<typeof extraBlockSchema>

/*
  Every field has a default, so a document written before sections existed
  parses into the full shape and nothing downstream has to ask whether a
  section has been decided about. The top of the page has no `enabled`: the
  page is the name.
*/
export const siteSectionsSchema = z.object({
  hero: z
    .object({
      variant: z.enum(HERO_VARIANTS).default('photoBackground'),
      photo: assetRefSchema.optional()
    })
    .default({}),
  /*
    The venue's menu, opened from a button into a sheet. The dishes are not
    here: they are resolved from the menu the venue keeps in the RMS every
    time the page is drawn, so the page never shows last month's prices.
  */
  menu: toggled
    .extend({
      variant: z.enum(MENU_VARIANTS).default('list'),
      showPrices: z.boolean().default(true),
      showPhotos: z.boolean().default(true)
    })
    .default({}),
  about: toggled
    .extend({
      variant: z.enum(ABOUT_VARIANTS).default('photoLeft'),
      photo: assetRefSchema.optional()
    })
    .default({}),
  visit: toggled
    .extend({ variant: z.enum(VISIT_VARIANTS).default('sideBySide') })
    .default({}),
  gallery: toggled
    .extend({
      variant: z.enum(GALLERY_VARIANTS).default('grid'),
      photos: z
        .array(assetRefSchema)
        .max(MAX_TEMPLATE_GALLERY_PHOTOS)
        .default([])
    })
    .default({}),
  reservations: toggled.default({}),
  map: toggled.extend({ height: z.enum(MAP_HEIGHTS).default('M') }).default({}),
  /*
    A button to the venue's Google review form. Its words are the owner's to
    change, in every language; left empty, the template's own are used.
  */
  review: toggled.extend({ label: localizedTextSchema.optional() }).default({}),
  extras: z.array(extraBlockSchema).max(MAX_TEMPLATE_EXTRAS).default([])
})

export type SiteSections = z.infer<typeof siteSectionsSchema>

/**
 * The facts about a venue, as the owner types them into the settings form.
 *
 * This is the whole product now: somebody fills this in, picks a template and
 * has a finished site. The page is always generated from these settings, never
 * edited apart from them, which is what lets a template be switched without
 * losing a word that was typed.
 *
 * The settings live inside the document rather than beside it, because the
 * renderer has to be able to draw a published page from the document alone.
 */
export const siteSettingsSchema = z.object({
  /** Not translated: a restaurant's name is its name in every language. */
  name: z.string().max(120),
  tagline: localizedTextSchema,
  description: localizedTextSchema,

  street: z.string().max(160),
  postalCode: z.string().max(16),
  city: z.string().max(80),

  phone: z.string().max(40),
  email: z.string().max(160),

  /** Seven rows, one per weekday, in the order the roster already counts them. */
  hours: z.array(openingHoursEntrySchema).max(7),

  templateId: z.enum(TEMPLATE_IDS).default('classic'),

  /*
    The photos by position, as documents were written before the sections
    existed: the first behind the name, the second beside the description, the
    rest the gallery. Read once by `normalizeSiteSettings`, which lifts them
    into the sections, and never written again.
  */
  photos: z.array(z.string().max(2048)).max(12).optional(),

  sections: siteSectionsSchema.default({}),

  /*
    The rooms a guest is shown, copied out of the floor planner when they were
    chosen. Copies for the same reason the element holds them: a published page
    has no session to fetch a plan with, and a table moved next month must not
    redraw a page from last month. Empty for a venue with no plan, which simply
    has no room section.
  */
  reservationRooms: z.array(roomPlanSchema).max(MAX_ROOMS_PER_BLOCK).optional(),

  facebookUrl: z.string().max(2048).optional(),
  instagramUrl: z.string().max(2048).optional(),

  /*
    The venue's Google place, as far as the page needs it: the map and the
    review form. Copied in from the connection made in Settings, like the
    address, and absent while the venue is not connected or Google no longer
    recognises the place.
  */
  googleLocation: z
    .object({
      // Empty while the backend has no embed key: the review link still works.
      embedUrl: z
        .string()
        .max(2048)
        .refine(url => url === '' || isGoogleMapsEmbedUrl(url), {
          message: 'not a Google Maps embed'
        }),
      reviewUrl: z.string().url().max(2048)
    })
    .optional()
})

export type SiteSettings = z.infer<typeof siteSettingsSchema>
